import React, { Component } from 'react'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  List,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  IconButton,
  // Grid
} from '@mui/material'
// import Autocomplete from '@mui/material/Autocomplete'
import DeleteIcon from '@mui/icons-material/Delete'
import { Col, Row } from 'reactstrap'
import moment from 'moment'
import algoliasearch from 'algoliasearch'
import { withFirebase } from '../Fb'
import PersonSearch from '../Inputs/PersonSearch'
import { Dialog, DialogActions, DialogTitle, DialogContent } from '@mui/material'
import ImageSelector from '../Inputs/ImageSelector'
import MembershipPricesEditor from '../Inputs/MembershipPricesEditor'
import ExtraInfoQuestionsEditor from '../Inputs/ExtraInfoQuestionsEditor'
import ActivityTimeRange from '../Inputs/ActivityTimeRange'
import DateTimeSelect from '../Inputs/DateTimeSelect'
import TimeSelect from '../Inputs/TimeSelect'
import { ORGANISER_NOTIFICATION_OPTIONS, ORGANISER_NOTIFICATION_DEFAULT, getOrganiserNotification } from '../utils/activityNotifications'
import {
  cancellationEndError,
  hasOwnCancellationEnd,
  joinMinutes,
  splitMinutes,
} from '../utils/activityCancellationDeadline'
import { TEMPLATE_DEFAULTS, formatDuration, shiftEntryTimes, templateError } from '../utils/activityTemplates'
import PersonAndOrganisationSearch from '../Inputs/PersonAndOrganisationSearch'

const algoliaclient = algoliasearch('6WV1D95IXL', 'd7e7d2238757ecd7bfd520b2b3a5562f')
const algoliaindex = algoliaclient.initIndex('falcon_combined')

const INTERVALS = [
  { label: 'Päivittäin', value: 'daily' },
  { label: 'Viikottain', value: 'weekly' },
  { label: 'Kuukausittain', value: 'monthly' },
]

const STEPS = ['Perustiedot', 'Ilmoittautuminen', 'Kuvaukset']
const LONGSTEPS = ['Perustiedot', 'Toistuvuus', 'Ilmoittautuminen', 'Kuvaukset']
const GENDERS = [
  { label: 'Avoin', value: 'open' },
  { label: 'Miehet', value: 'men' },
  { label: 'Naiset', value: 'female' },
]
const STATES = [
  { label: 'Luonnos', value: 'draft' },
  { label: 'Julkaistu', value: 'published' },
  { label: 'Peruttu', value: 'cancelled' },
  { label: 'Poistettu', value: 'deleted' },
]

const WEEKDAYS = [
  { label: 'Maanantai', value: 1 },
  { label: 'Tiistai', value: 2 },
  { label: 'Keskiviikko', value: 3 },
  { label: 'Torstai', value: 4 },
  { label: 'Perjantai', value: 5 },
  { label: 'Lauantai', value: 6 },
  { label: 'Sunnuntai', value: 7 },
]

function toHoursAndMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return hours + 'h, '  + minutes + 'mins'
}

const DISABLED_VAT_OPTIONS = [10, 14, 24]

const EMPTY_ACTIVITY = {
  name: { fi: '', sv: '', en: '' },
  description: { fi: '', sv: '', en: '' },
  ingress: { fi: '', sv: '', en: '' },
  location: { fi: '', sv: '', en: '' },
  price: 0,
  variations: [],
  membershipPrices: [],
  extrainfo: [],
  poster: null,
  type: '',
  gender: 'open',
  ageGroup: '',
  skillLevel: '',
  start: '12:00',
  end: '13:00',
  entrystart: moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm'),
  entryend: null,
  // own deadline for cancelling a participation; null follows entryend
  cancellationend: null,
  isexternal: false,
  externalurl: '',
  maxp: 0,
  minp: 0,
  public: true,
  tags: [],
  categories: [],
  hasqueue: false,
  sport: '',
  billable: false,
  ispublished: false,
  organiser: null,
  organiserNotification: ORGANISER_NOTIFICATION_DEFAULT,
  state: 'draft',
  vat: 13.5,
  accounting: '',
  productCode: '',
  autoCancellation: false,
  // set here too so a series gets them on every occurrence, not by editing each one later
  showusers: false,
  hideentrycount: false,
  pinned: false,
  entryendProcessed: false,
}

const emptyActivity = () => ({ ...JSON.parse(JSON.stringify(EMPTY_ACTIVITY)), membershipPrices: [], extrainfo: [] })

// Enrolment closes this long before the activity starts.
const ENTRYEND_HOURS_BEFORE_START = 12

const EMPTY_RECURRENT_END = {
  days: 0,
  hours: ENTRYEND_HOURS_BEFORE_START,
  minutes: 0,
}

const EMPTY_RECURRENT_START = {
  days: 10,
  hours: 0,
  minutes: 0,
}

class ActivityCreator extends Component {
  constructor(props) {
    super(props)
    // EMPTY_ACTIVITY is spread shallowly, so the arrays need their own identity.
    // initialActivity is a pre-filled copy (see utils/activityCopy) and is already
    // deep cloned, so it can override the defaults directly.
    // Deep: name/description/ingress are edited in place, and a shallow spread would let
    // them leak into the next new activity of the session.
    const base = emptyActivity()
    const activity = this.props.initialActivity ? { ...base, ...this.props.initialActivity } : base
    if (this.props.template) {
      // A template has no dates: the time pickers' values are replaced by its duration and
      // the relative entry/cancellation minutes (docs/features/activity-templates.md).
      delete activity.start
      delete activity.end
      // The empty activity carries datetimes in entrystart/entryend; a template needs minutes,
      // so anything that is not a number falls back to the default (cancellationend may be null).
      const MINUTE_FIELDS = ['duration', 'entrystart', 'entryend', 'cancellationend']
      Object.keys(TEMPLATE_DEFAULTS).forEach((key) => {
        const value = activity[key]
        const missing = MINUTE_FIELDS.includes(key)
          ? !(typeof value === 'number' && isFinite(value)) && !(key === 'cancellationend' && value === null)
          : value === undefined || value === null
        if (missing) activity[key] = Array.isArray(TEMPLATE_DEFAULTS[key]) ? [] : TEMPLATE_DEFAULTS[key]
      })
      if (activity.templatename === undefined) activity.templatename = (activity.name && activity.name.fi) || ''
    }
    // EMPTY_ACTIVITY seeds start/end as bare times ('12:00'), which the recurring step
    // wants but the one-off step does not -- there they are full datetimes. The old
    // datetime-local input papered over this with a display-only fallback, so an
    // untouched form saved '12:00'. Seed real values instead, so what is shown is what
    // is stored. Skipped for a copy, which deliberately starts with blank times.
    if (this.props.template) {
      // nothing to seed: the template keeps its relative values
    } else if (!this.props.recurrent && !this.props.initialActivity) {
      const start = moment().add(1, 'hour').startOf('hour')
      activity.start = start.format('YYYY-MM-DDTHH:mm')
      activity.end = start.clone().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
      activity.entryend = start.clone().subtract(ENTRYEND_HOURS_BEFORE_START, 'hours').format('YYYY-MM-DDTHH:mm')
    } else if (!this.props.recurrent && activity.start && !activity.entryend) {
      // pre-filled times without an entry end, e.g. a slot picked in the activity calendar
      activity.entryend = moment(activity.start).subtract(ENTRYEND_HOURS_BEFORE_START, 'hours').format('YYYY-MM-DDTHH:mm')
    }
    this.state = {
      activeStep: 0,
      activity,
      // the days/hours/minutes fields of the relative entry times; a template being edited
      // starts from its own values
      recurrentEntryEnd: this.props.template ? splitMinutes(activity.entryend) : { ...EMPTY_RECURRENT_END },
      recurrentEntryStart: this.props.template ? splitMinutes(activity.entrystart) : { ...EMPTY_RECURRENT_START },

      activitytypes: this.props.activitytypes,
      sports: this.props.sports,
      steps: this.props.recurrent ? LONGSTEPS : STEPS, // a template uses the one-off steps
      recurrencyStartWeekday: this.props.initialWeekday || 1, // 1-7 isoWeekday
      posterEditOpen: false,
      priceInputs: { price: Number(activity.price || 0).toFixed(2) },
      memberships: [],
    }
    if (this.props.recurrent) {
      // A series pre-filled from an activity template (recurrentFromTemplate) already has
      // its recurrence and relative minutes; only missing ones get the defaults.
      const isMinutes = (value) => typeof value === 'number' && isFinite(value)
      if (!this.state.activity.recurrency || typeof this.state.activity.recurrency !== 'object') {
        this.state.activity.recurrency = {
          interval: 'weekly',
          startdate: moment().format('YYYY-MM-DD'),
          enddate: null,
        }
      }
      if (!isMinutes(this.state.activity.entrystart)) this.state.activity.entrystart = 10 * 24 * 60
      if (!isMinutes(this.state.activity.entryend)) this.state.activity.entryend = ENTRYEND_HOURS_BEFORE_START * 60
      if (this.props.initialActivity) {
        this.state.recurrentEntryStart = splitMinutes(this.state.activity.entrystart)
        this.state.recurrentEntryEnd = splitMinutes(this.state.activity.entryend)
      }
    }
  }

  componentDidMount() {
    this.props.firebase
      .getMemberships()
      .then((snap) => {
        const memberships = snap.empty ? [] : snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        this.setState({ memberships })
      })
      .catch((error) => console.error(error))
  }

  // membershipPrices/extrainfo are arrays owned by their editor components.
  handleArrayChange = (value, property) => {
    const { activity } = this.state
    activity[property] = value
    this.setState({ activity })
  }

  handleSave = () => {
    const { activity, priceInputs } = this.state
    if (!hasOwnCancellationEnd(activity)) activity.cancellationend = null
    if (priceInputs.price) {
      // clear price 
      let _price = priceInputs.price.replace(',', '.').replace(/[^0-9.]/g, '')
      activity.price = parseFloat(_price)
    } else {
      activity.price = 0
    }
    // A series' occurrence rows carry their own times and title, written when the dates were
    // generated. Bring them up to the current times and name, keeping each row's date - rows
    // removed by hand stay removed.
    if (this.props.recurrent && Array.isArray(activity.recurrencies) && activity.start && activity.end) {
      activity.recurrencies = activity.recurrencies.map((row) => {
        const date = String(row.starttime || '').slice(0, 10)
        if (!date) return row
        return { ...row, title: (activity.name && activity.name.fi) || row.title, starttime: date + 'T' + activity.start, endtime: date + 'T' + activity.end }
      })
    }
    // onSaved lets the caller move on once the write has landed (e.g. the template list)
    Promise.resolve(this.props.onCreate(activity))
      .then(() => this.props.onSaved && this.props.onSaved())
      .catch((error) => console.error('Saving failed', error))
  }

  // Inputs that emit a value rather than an event (DateTimeSelect).
  handleValueChange = (value, property) => {
    const { activity } = this.state
    activity[property] = value
    this.setState({ activity })
  }

  // The range picker sets both ends together, so it does not go through
  // handleTextChange (which handles one property at a time).
  handleTimeRangeChange = ({ start, end }) => {
    let { activity } = this.state
    // followStart: an activity built from a template keeps its enrolment and cancellation
    // times at the template's distance from the start when the start moves.
    if (this.props.followStart && start && activity.start && start !== activity.start) {
      activity = shiftEntryTimes(activity, activity.start, start)
    }
    activity.start = start
    activity.end = end
    // Same rule as handleTextChange: enrolment closes 12 h before the start, unless a
    // deadline has already been set by hand.
    if (start && !activity.entryend) {
      activity.entryend = moment(start).subtract(ENTRYEND_HOURS_BEFORE_START, 'hours').format('YYYY-MM-DDTHH:mm')
    }
    this.setState({ activity })
  }

  // Nobody to notify means the notification setting has nothing to say, so it stays
  // hidden until a coach is picked.
  hasOrganisers = () => {
    const organisers = this.state.activity.organisers
    return !!(organisers && organisers.length > 0)
  }

  // Price as currently typed, so a percentage row previews against the live value.
  getEditedPrice = () => {
    const raw = this.state.priceInputs.price
    if (raw !== undefined && raw !== null && raw !== '') {
      const parsed = parseFloat(String(raw).replace(',', '.').replace(/[^0-9.]/g, ''))
      if (isFinite(parsed)) return parsed
    }
    return this.state.activity.price || 0
  }

  handleNext = () => {
    if (this.state.activeStep + 1 === this.state.steps.length) {
      this.handleSave()
    }
    this.setState({ activeStep: this.state.activeStep + 1 })
  }

  handleBack = () => {
    this.setState({ activeStep: this.state.activeStep - 1 })
  }

  handleReset = () => {
    this.setState({ activeStep: 0, activity: emptyActivity(), 
      posterEditOpen: false, 
      recurrencyStartWeekday: 1, 
      recurrentEntryEnd: { ...EMPTY_RECURRENT_END }, 
      recurrentEntryStart: { ...EMPTY_RECURRENT_START } 
    })
  }

  handlePosterChange = (e) => {
    const { activity } = this.state
    const _image = this.props.images.find((image) => image.id === e.target.value)
    activity.poster = _image.url
    activity.media = _image
    this.setState({ activity })
  }

  updateRecurrency = (activity) => {
    const { recurrencyStartWeekday } = this.state
    if (activity.recurrency) {
      const { startdate, enddate, interval } = activity.recurrency
      if (startdate && enddate && interval && recurrencyStartWeekday) {
        const d = { title: activity.name.fi }
        const start = moment(startdate)
        if (start.isoWeekday() !== recurrencyStartWeekday) {
          // find next date where weekday is correct
          start.add(1, 'days')
          while (start.isoWeekday() !== recurrencyStartWeekday) {
            start.add(1, 'days')
          }
        }
        const end = moment(enddate)
        const recurrencies = []
        if (interval === 'weekly') {
          for (let m = moment(start); m.isBefore(end); m.add(1, 'weeks')) {
            const dd = { ...d }
            dd.starttime = m.format('YYYY-MM-DD') + 'T' + activity.start
            dd.endtime = m.format('YYYY-MM-DD') + 'T' + activity.end
            recurrencies.push(dd)
          }
        } else if (interval === 'monthly') {
          for (let m = moment(start); m.isBefore(end); m.add(1, 'months')) {
            const dd = { ...d }
            dd.starttime = m.format('YYYY-MM-DD') + 'T' + activity.start
            dd.endtime = m.format('YYYY-MM-DD') + 'T' + activity.end
            recurrencies.push(dd)
          }
        } else if (interval === 'daily') {
          for (let m = moment(start); m.isBefore(end); m.add(1, 'days')) {
            const dd = { ...d }
            dd.starttime = m.format('YYYY-MM-DD') + 'T' + activity.start
            dd.endtime = m.format('YYYY-MM-DD') + 'T' + activity.end
            recurrencies.push(dd)
          }
        }
        return recurrencies
      }
    }
    return null
  }

  handleTextChange = (e, property) => {
    const { activity } = this.state
    console.log(activity)
    if (property.indexOf('.') > -1) {
      const [parent, child] = property.split('.')
      activity[parent][child] = e.target.value
    } else {
      activity[property] = e.target.value
    }
    if (property === 'start') {
      if (!activity.end) {
        activity.end = moment(e.target.value).add(1, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm')
      }
      // Enrolment closes 12 h before the activity starts. Anchored to start rather
      // than to entrystart, which could put the deadline after the activity itself.
      if (!activity.entryend) {
        activity.entryend = moment(e.target.value).subtract(ENTRYEND_HOURS_BEFORE_START, 'hours').format('YYYY-MM-DDTHH:mm')
      }
    }
    if (this.props.recurrent && property.indexOf('recurrency.') > -1) {
      const recurrencies = this.updateRecurrency(activity)
      if (recurrencies) {
        activity.recurrencies = recurrencies
      }
    }
    this.setState({ activity })
  }


  handlePriceChange = (e, property) => {
    const { priceInputs } = this.state
    priceInputs[property] = e.target.value
    this.setState({ priceInputs })
  }

  handleNumberChange = (e, property) => {
    const { activity } = this.state
    if (typeof e.target.value !== 'number') {
      let value = e.target.value
      if (value.indexOf(',') > -1) {
        value = value.replace(',', '.')
      }

      value = value.replace(/[^0-9.]/g, '')
      if (property.indexOf('.') > -1) {
        const [parent, child] = property.split('.')
        activity[parent][child] = parseFloat(value)
      } else {
        activity[property] = parseFloat(value)
      }
    } else {
      if (property.indexOf('.') > -1) {
        const [parent, child] = property.split('.')
        activity[parent][child] = e.target.value
      } else {
        activity[property] = e.target.value
      }
    }
    this.setState({ activity })
  }

  handleSwitchChange = (e, property) => {
    const { activity } = this.state
    if (property.indexOf('.') > -1) {
      const [parent, child] = property.split('.')
      activity[parent][child] = e.target.checked
    } else {
      activity[property] = e.target.checked
    }
    this.setState({ activity })
  }

  // The occurrence dates depend on the weekday, so a changed weekday regenerates them (once
  // the dates are complete), the same as a changed interval or start/end date does.
  handleRecurrentWeekdayChange = (value) => {
    this.setState({ recurrencyStartWeekday: value }, () => {
      const recurrencies = this.updateRecurrency(this.state.activity)
      if (recurrencies) this.setState({ activity: { ...this.state.activity, recurrencies } })
    })
  }

  handleOptionLabel = (opt) => {
    return opt.firstname && opt.lastname ? opt.firstname + ' ' + opt.lastname : 'Tyhjä'
  }

  clearAlgolia = (x) => {
    x.id = x.objectID
    delete x._highlightResult
    delete x.lastmodified
    delete x.reservationAlert
    delete x.objectID
    return x
  }

  searchParticipants = (input) => {
    algoliaindex.search(input, { filters: '_tags:' + this.props.firebase.customerid }).then(({ hits }) => {
      const searchresults = hits
        .filter((x) => x.email && x.email.indexOf('_reg') < 0 && x.email.indexOf('_del') < 0)
        .map((x) => this.clearAlgolia(x))
      this.setState({ searchresults })
    })
  }

  handleParticipantChange = (event, value) => {
    console.log('handleParticipantChange', value)
    const { activity } = this.state
    if (!activity.organisers) {
      activity.organisers = []
    }
    if (value) {
      activity.organisers = value.map((x) => {
        return { id: x.id, firstname: x.firstname, lastname: x.lastname, email: x.email, phone: x.phone || '' }
      })
      console.log(activity.organisers)
    }
    this.setState({ activity })
  }


  handleDurationChange = (e, unit) => {
    const value = parseInt(String(e.target.value).replace(/[^0-9]/g, '')) || 0
    // from the previous state: the hours and minutes fields each keep the other's part
    this.setState((prev) => {
      const current = Number(prev.activity.duration) || 0
      const hours = unit === 'hours' ? value : Math.floor(current / 60)
      const minutes = unit === 'minutes' ? value : current % 60
      return { activity: { ...prev.activity, duration: hours * 60 + minutes } }
    })
  }

  // Template mode: the list name and the duration take the place of the date and times.
  renderTemplateTiming = () => {
    const { activity } = this.state
    const duration = Number(activity.duration) || 0
    return (
      <Row className='mt-4'>
        <Col xs='12' md='6'>
          <TextField
            fullWidth
            size='small'
            id='templatename'
            label='Pohjan nimi'
            helperText='Näkyy pohjalistassa ja kalenterin vieressä'
            value={activity.templatename || ''}
            onChange={(e) => this.setState({ activity: { ...activity, templatename: e.target.value } })}
          />
        </Col>
        <Col xs='12' md='6'>
          <Typography variant='subtitle2'>Kesto</Typography>
          <TextField
            style={{ width: 60 }}
            id='duration-hours'
            label='tuntia'
            type='number'
            size='small'
            value={Math.floor(duration / 60)}
            onChange={(e) => this.handleDurationChange(e, 'hours')}
            InputLabelProps={{ shrink: true }}
          />{' '}
          <TextField
            style={{ width: 60 }}
            id='duration-minutes'
            label='minuuttia'
            type='number'
            size='small'
            value={duration % 60}
            onChange={(e) => this.handleDurationChange(e, 'minutes')}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant='caption' display='block'>
            {formatDuration(duration)}. Päivä ja alkamisaika valitaan, kun pohja pudotetaan kalenteriin.
          </Typography>
        </Col>
      </Row>
    )
  }

  renderBasicInfo = () => {
    return (
      <div>
        <Row>
          <Col xs='12' md='6'>
            <TextField
              fullWidth
              required
              id='name-required'
              label='Nimi'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'name.fi')}
              value={this.state.activity.name.fi}
            />
          </Col>
        </Row>
        {this.props.template ? (
          this.renderTemplateTiming()
        ) : (
          <Row className='mt-4'>
            <Col xs='12' md='10'>
              <ActivityTimeRange
                start={this.state.activity.start}
                end={this.state.activity.end}
                onChange={this.handleTimeRangeChange}
              />
            </Col>
          </Row>
        )}
        <Row className='mt-4'>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              required
              id='price-required'
              label='Hinta'
              size='small'
              onChange={(e) => this.handlePriceChange(e, 'price')}
              InputProps={{
                endAdornment: <InputAdornment position='end'>€</InputAdornment>,
              }}
              value={this.state.priceInputs.price}
            />
          </Col>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              id='standard-select-vat'
              select
              required
              size='small'
              label='Alv%'
              value={this.state.activity.vat}
              onChange={(e) => this.handleNumberChange(e, 'vat')}
            >
              {[0, 10, 13.5, 14, 24, 25.5].map((option) => (
                <MenuItem key={option} value={option} disabled={DISABLED_VAT_OPTIONS.includes(option)}>
                  {option} %
                </MenuItem>
              ))}
            </TextField>
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='10'>
            <Typography variant='subtitle2' gutterBottom>
              Jäsenhinnat
            </Typography>
            <MembershipPricesEditor
              value={this.state.activity.membershipPrices}
              memberships={this.state.memberships}
              activityPrice={this.getEditedPrice()}
              onChange={(value) => this.handleArrayChange(value, 'membershipPrices')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              size='small'
              id='standard-select-type'
              select
              required
              label='Tyyppi'
              value={this.state.activity.type}
              helperText='Aktiviteetin tyyppi'
              onChange={(e) => this.handleTextChange(e, 'type')}
            >
              {this.state.activitytypes.map((option) => (
                <MenuItem key={option.key} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              size='small'
              id='standard-select-sport'
              select
              required
              label='Laji'
              value={this.state.activity.sport}
              helperText='Lajin valinta'
              onChange={(e) => this.handleTextChange(e, 'sport')}
            >
              {this.state.sports.map((option) => (
                <MenuItem key={option.key} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              label='Taso'
              value={this.state.activity.skillLevel || ''}
              helperText='Vapaa teksti, esim. Alkeet tai ⭐⭐⭐'
              onChange={(e) => this.handleTextChange(e, 'skillLevel')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <FormControl>
              <FormLabel id='activity-gender-label' sx={{ fontSize: 12 }}>
                Sukupuoli
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby='activity-gender-label'
                value={this.state.activity.gender || 'open'}
                onChange={(e) => this.handleTextChange(e, 'gender')}
              >
                {GENDERS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size='small' />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Col>
        </Row>
        <Row>
          <Col className='mt-4' xs='12' md='6'>
            <Typography variant='body2'>Sisäinen tapahtumaluokka (käytetään etukoodien kohdentamiseen)</Typography>
            <TextField
              id='providertype'
              label=''
              size='small'
              onChange={(e) => this.handleTextChange(e, 'providertype')}
              value={this.state.activity.providertype}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <PersonSearch onChange={this.handleParticipantChange} values={this.state.activity.organisers || null} />
          </Col>
        </Row>
        {this.hasOrganisers() ? (
          <Row className='mt-4'>
            <Col xs={12} md={4}>
              <TextField
                id='standard-select-organisernotification'
                select
                fullWidth
                size='small'
                label='Ilmoitus ohjaajalle osallistujamuutoksista'
                value={getOrganiserNotification(this.state.activity)}
                onChange={(e) => this.handleTextChange(e, 'organiserNotification')}
              >
                {ORGANISER_NOTIFICATION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={12} md={4}>
            <TextField
              fullWidth
              id='productCode'
              label='Tuotekoodi'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'productCode')}
              value={this.state.activity.productCode}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <TextField
              fullWidth
              id='accounting'
              label='Kirjanpitotili'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'accounting')}
              value={this.state.activity.accounting}
            />
          </Col>
        </Row>
      </div>
    )
  }

  renderRecurrentBasicInfo = () => {
    return (
      <div>
        <Row>
          <Col xs='12' md='6'>
            <TextField
              fullWidth
              required
              id='name-required'
              label='Nimi'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'name.fi')}
              value={this.state.activity.name.fi}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <TextField
              required
              id='start-weekday'
              label='Päivä'
              select
              value={this.state.recurrencyStartWeekday || 1}
              onChange={(e) => this.handleRecurrentWeekdayChange(e.target.value)}
            >
              {WEEKDAYS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='6' md='2'>
            <TimeSelect
              id='recurrent-starttime'
              label='Alkuaika'
              value={this.state.activity.start || ''}
              onChange={(value) => this.handleValueChange(value, 'start')}
            />
          </Col>
          <Col xs='6' md='2'>
            <TimeSelect
              id='recurrent-endtime'
              label='Loppuaika'
              value={this.state.activity.end || ''}
              onChange={(value) => this.handleValueChange(value, 'end')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              required
              id='price-required'
              label='Hinta'
              size='small'
              onChange={(e) => this.handlePriceChange(e, 'price')}
              InputProps={{
                endAdornment: <InputAdornment position='end'>€</InputAdornment>,
              }}
              value={this.state.priceInputs.price}
            />
          </Col>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              id='standard-select-vat'
              select
              required
              size='small'
              label='Alv%'
              value={this.state.activity.vat}
              onChange={(e) => this.handleNumberChange(e, 'vat')}
            >
              {[0, 10, 13.5, 14, 24, 25.5].map((option) => (
                <MenuItem key={option} value={option} disabled={DISABLED_VAT_OPTIONS.includes(option)}>
                  {option} %
                </MenuItem>
              ))}
            </TextField>
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='10'>
            <Typography variant='subtitle2' gutterBottom>
              Jäsenhinnat
            </Typography>
            <MembershipPricesEditor
              value={this.state.activity.membershipPrices}
              memberships={this.state.memberships}
              activityPrice={this.getEditedPrice()}
              onChange={(value) => this.handleArrayChange(value, 'membershipPrices')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              size='small'
              id='standard-select-type'
              select
              required
              label='Tyyppi'
              value={this.state.activity.type}
              helperText='Aktiviteetin tyyppi'
              onChange={(e) => this.handleTextChange(e, 'type')}
            >
              {this.state.activitytypes.map((option) => (
                <MenuItem key={option.key} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='6' md='2'>
            <TextField
              fullWidth
              size='small'
              id='standard-select-sport'
              select
              required
              label='Laji'
              value={this.state.activity.sport}
              helperText='Lajin valinta'
              onChange={(e) => this.handleTextChange(e, 'sport')}
            >
              {this.state.sports.map((option) => (
                <MenuItem key={option.key} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              label='Taso'
              value={this.state.activity.skillLevel || ''}
              helperText='Vapaa teksti, esim. Alkeet tai ⭐⭐⭐'
              onChange={(e) => this.handleTextChange(e, 'skillLevel')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <FormControl>
              <FormLabel id='activity-gender-label' sx={{ fontSize: 12 }}>
                Sukupuoli
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby='activity-gender-label'
                value={this.state.activity.gender || 'open'}
                onChange={(e) => this.handleTextChange(e, 'gender')}
              >
                {GENDERS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size='small' />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Col>
        </Row>
        <Row>
          <Col className='mt-4' xs='12' md='6'>
            <Typography variant='body2'>Sisäinen tapahtumaluokka (käytetään etukoodien kohdentamiseen)</Typography>
            <TextField
              id='providertype'
              label=''
              size='small'
              onChange={(e) => this.handleTextChange(e, 'providertype')}
              value={this.state.activity.providertype}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <PersonSearch onChange={this.handleParticipantChange} values={this.state.activity.organisers || null} />
          </Col>
        </Row>
        {this.hasOrganisers() ? (
          <Row className='mt-4'>
            <Col xs={12} md={4}>
              <TextField
                id='standard-select-organisernotification'
                select
                fullWidth
                size='small'
                label='Ilmoitus ohjaajalle osallistujamuutoksista'
                value={getOrganiserNotification(this.state.activity)}
                onChange={(e) => this.handleTextChange(e, 'organiserNotification')}
              >
                {ORGANISER_NOTIFICATION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={12} md={4}>
            <TextField
              fullWidth
              id='productCode-required'
              label='Tuotekoodi'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'productCode')}
              value={this.state.activity.productCode}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <TextField
              fullWidth
              id='accounting-required'
              label='Kirjanpitotili'
              size='small'
              onChange={(e) => this.handleTextChange(e, 'accounting')}
              value={this.state.activity.accounting}
            />
          </Col>
        </Row>
      </div>
    )
  }

  removeRecurrency = (index) => {
    const recurrencies = this.state.activity.recurrencies
    recurrencies.splice(index, 1)
    this.setState({ activity: { ...this.state.activity, recurrencies } })
  }

  renderRecurrencyInfo = () => {
    return (
      <div>
        <Row>
          <Col xs='12' md='4'>
            <Row>
              <Col xs='12' md='6'>
                <Typography variant='subtitle2'>Toistuvuus</Typography>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <TextField
                  id='standard-select-recurrency'
                  select
                  required
                  label='Toistuvuus'
                  value={this.state.activity?.recurrency?.interval || 'weekly'}
                  helperText='Aktiviteetin toistuvuus'
                  onChange={(e) => this.handleTextChange(e, 'recurrency.interval')}
                >
                  {INTERVALS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <TextField
                  required
                  id='start-date'
                  label='Alkupäivä'
                  type='date'
                  variant='standard'
                  value={
                    this.state.activity?.recurrency?.startdate ||
                    moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DD')
                  }
                  // className={classes.textField}
                  onChange={(e) => this.handleTextChange(e, 'recurrency.startdate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <TextField
                  required
                  id='end-datetime-local'
                  label='Loppupäivä'
                  type='date'
                  variant='standard'
                  value={this.state.activity?.recurrency?.enddate || ''}
                  onChange={(e) => this.handleTextChange(e, 'recurrency.enddate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Col>
            </Row>
          </Col>
          <Col xs='12' md='3'>
            <List>
              {this.state.activity?.recurrencies
                ? this.state.activity.recurrencies.map((recurrency, index) => {
                    return (
                      <ListItem key={index}>
                        <ListItemText primary={moment(recurrency.starttime).format('DD.MM.YYYY')} />
                        <ListItemSecondaryAction>
                          <IconButton edge='end' aria-label='delete' onClick={() => this.removeRecurrency(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    )
                  })
                : null}
            </List>
          </Col>
        </Row>
      </div>
    )
  }

  handleRecurrentEntryEndChange = (e, unit) => {
    const { activity } = this.state
    let value = parseInt(e.target.value.replace(/[^0-9]/g, '').replace('.', '')) || 0
    if (unit === 'days') {
      const entryend = this.state.recurrentEntryEnd.minutes + this.state.recurrentEntryEnd.hours * 60 + value * 24 * 60
      this.setState({ recurrentEntryEnd: { ...this.state.recurrentEntryEnd, days: value }, activity: { ...activity, entryend } })
    } else if (unit === 'hours') {
      const entryend = this.state.recurrentEntryEnd.minutes + value * 60 + this.state.recurrentEntryEnd.days * 24 * 60
      this.setState({ recurrentEntryEnd: { ...this.state.recurrentEntryEnd, hours: value }, activity: { ...activity, entryend } })
    } else if (unit === 'minutes') {
      const entryend = value + this.state.recurrentEntryEnd.hours * 60 + this.state.recurrentEntryEnd.days * 24 * 60
      this.setState({ recurrentEntryEnd: { ...this.state.recurrentEntryEnd, minutes: value }, activity: { ...activity, entryend } })
    }
  }

  handleRecurrentEntryStartChange = (e, unit) => {
    const { activity } = this.state
    let value = parseInt(e.target.value.replace(/[^0-9]/g, '').replace('.', '')) || 0
    if (unit === 'days') {
      const entrystart = this.state.recurrentEntryStart.minutes + this.state.recurrentEntryStart.hours * 60 + value * 24 * 60
      this.setState({ recurrentEntryStart: { ...this.state.recurrentEntryStart, days: value }, activity: { ...activity, entrystart } })
    } else if (unit === 'hours') {
      const entrystart = this.state.recurrentEntryStart.minutes + value * 60 + this.state.recurrentEntryStart.days * 24 * 60
      this.setState({ recurrentEntryStart: { ...this.state.recurrentEntryStart, hours: value }, activity: { ...activity, entrystart } })
    } else if (unit === 'minutes') {
      const entrystart = value + this.state.recurrentEntryStart.hours * 60 + this.state.recurrentEntryStart.days * 24 * 60
      this.setState({ recurrentEntryStart: { ...this.state.recurrentEntryStart, minutes: value }, activity: { ...activity, entrystart } })
    }
  }


  // Own deadline for cancelling a participation (docs/features/activity-cancellation-deadline.md).
  // Off, it follows the entry end; turned on, it starts from the entry end.
  toggleCancellationEnd = (e) => {
    const { activity } = this.state
    this.setState({ activity: { ...activity, cancellationend: e.target.checked ? activity.entryend : null } })
  }

  handleRecurrentCancellationEndChange = (e, unit) => {
    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0
    const parts = { ...splitMinutes(this.state.activity.cancellationend), [unit]: value }
    this.setState({ activity: { ...this.state.activity, cancellationend: joinMinutes(parts) } })
  }

  // Recurring templates and activity templates store the entry/cancellation times as
  // minutes before the start; a one-off activity stores datetimes.
  isRelative = () => !!(this.props.recurrent || this.props.template)

  cancellationEndError = () => cancellationEndError(this.state.activity, { recurrent: this.isRelative() })

  formError = () => (this.props.template ? templateError(this.state.activity) : this.cancellationEndError())

  renderCancellationEnd = () => {
    const { activity } = this.state
    const own = hasOwnCancellationEnd(activity)
    const error = this.formError()
    const parts = splitMinutes(activity.cancellationend)
    return (
      <Row className='mt-4'>
        <Col xs='12' md='8'>
          <FormControlLabel
            control={<Switch checked={own} onChange={this.toggleCancellationEnd} />}
            label='Erillinen peruutuksen takaraja'
          />
          <Typography variant='caption' color='textSecondary' display='block'>
            {own
              ? 'Osallistumisen voi perua tähän asti. Takaraja ei voi olla ilmoittautumisen päättymisen jälkeen.'
              : 'Pois päältä: osallistumisen voi perua niin kauan kuin ilmoittautuminen on auki.'}
          </Typography>
          {own && this.isRelative() ? (
            <div className='mt-2'>
              {[['days', 'päivää'], ['hours', 'tuntia'], ['minutes', 'minuuttia']].map(([unit, label]) => (
                <React.Fragment key={unit}>
                  <TextField
                    style={{ width: 40 }}
                    id={'cancellationend-' + unit}
                    label={label}
                    type='number'
                    value={parts[unit]}
                    onChange={(e) => this.handleRecurrentCancellationEndChange(e, unit)}
                    InputLabelProps={{ shrink: true }}
                  />{' '}
                </React.Fragment>
              ))}
              <Typography variant='caption' display='block'>
                {toHoursAndMinutes(activity.cancellationend)} ennen tapahtumaa
              </Typography>
            </div>
          ) : null}
          {own && !this.isRelative() ? (
            <div className='mt-2'>
              <DateTimeSelect
                label='Peruutus päättyy'
                value={activity.cancellationend}
                onChange={(value) => this.handleValueChange(value, 'cancellationend')}
              />
            </div>
          ) : null}
          {error ? (
            <Typography variant='caption' color='error' display='block'>
              {error}
            </Typography>
          ) : null}
        </Col>
      </Row>
    )
  }

  renderEntryInfo = () => {
    return (
      <div>
        <Row>
          <Col xs='12' md='6'>
            <Typography variant='subtitle2'>Ilmoittautumisen ajat</Typography>
          </Col>
        </Row>
        {this.isRelative() ? (
          <>
                        <Row className='mt-4'>
              <Col xs='12' md='6'>
                <Typography variant='subtitle2'>Ilmoittautumisen alkaminen</Typography>
                <Typography variant='caption'>Ilmoittautuminen alkaa kun aktiviteetin alkuun</Typography>
                <div>
                <TextField
                  style={{ width: 40 }}
                  id='entrystart-number-days'
                  label='päivää'
                  type='number'
                  value={this.state.recurrentEntryStart.days || 0}
                  onChange={(e) => this.handleRecurrentEntryStartChange(e, 'days')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />{' '}
                <TextField
                  style={{ width: 40 }}
                  id='entrystart-number-hours'
                  label='tuntia'
                  type='number'
                  value={this.state.recurrentEntryStart.hours || 0}
                  onChange={(e) => this.handleRecurrentEntryStartChange(e, 'hours')}
                />{' '}
                <TextField
                  style={{ width: 40 }}
                  id='entrystart-number-minutes'
                  label='minuuttia'
                  type='number'
                  value={this.state.recurrentEntryStart.minutes || 0}
                  onChange={(e) => this.handleRecurrentEntryStartChange(e, 'minutes')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  />
                  </div>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <Typography variant='caption'>{toHoursAndMinutes(this.state.activity.entrystart)} ennen tapahtumaa</Typography>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <Typography variant='subtitle2'>Ilmoittautumisen päättyminen</Typography>
                <Typography variant='caption'>Ilmoittautuminen päättyy kun aktiviteetin alkuun</Typography>
                <div>
                <TextField
                  style={{ width: 40 }}
                  id='entryend-number'
                  label='päivää'
                  type='number'
                  value={this.state.recurrentEntryEnd.days || 0}
                  onChange={(e) => this.handleRecurrentEntryEndChange(e, 'days')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />{' '}
                <TextField
                  style={{ width: 40 }}
                  id='entryend-number'
                  label='tuntia'
                  type='number'
                  value={this.state.recurrentEntryEnd.hours || 0}
                  onChange={(e) => this.handleRecurrentEntryEndChange(e, 'hours')}
                />{' '}
                <TextField
                  style={{ width: 40 }}
                  id='entryend-number'
                  label='minuuttia'
                  type='number'
                  value={this.state.recurrentEntryEnd.minutes || 0}
                  onChange={(e) => this.handleRecurrentEntryEndChange(e, 'minutes')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  />
                  </div>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <Typography variant='caption'>{toHoursAndMinutes(this.state.activity.entryend)} ennen tapahtumaa</Typography>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Col xs='12' md='8'>
                <DateTimeSelect
                  label='Ilmoittautuminen alkaa'
                  value={this.state.activity.entrystart}
                  onChange={(value) => this.handleValueChange(value, 'entrystart')}
                />
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='8'>
                <DateTimeSelect
                  label='Ilmoittautuminen päättyy'
                  value={this.state.activity.entryend}
                  onChange={(value) => this.handleValueChange(value, 'entryend')}
                />
              </Col>
            </Row>
          </>
        )}
        {this.renderCancellationEnd()}
        <Row className='mt-5'>
          <Col xs='12' md='6'>
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.activity.isexternal}
                  onChange={(e) => this.handleSwitchChange(e, 'isexternal')}
                />
              }
              label='Ulkoinen ilmoittautuminen'
            />
          </Col>
        </Row>
        {this.state.activity.isexternal ? (
          <Row className='mt-4'>
            <Col xs='12' md='6'>
              <TextField
                required
                id='externalurl'
                label='Ilmoittautumisen ulkoisen linkin osoite'
                size='small'
                value={this.state.activity.externalurl}
                onChange={(e) => this.handleTextChange(e, 'externalurl')}
              />
            </Col>
          </Row>
        ) : (
          <React.Fragment>
            <Row>
              <Col xs='12' md='6'>
                <Typography variant='subtitle2'>Osallistujamäärät</Typography>
              </Col>
            </Row>

            <Row className='mt-4'>
              <Col xs='6' md='2'>
                <TextField
                  fullWidth
                  required
                  id='minp-required'
                  label='Minimimäärä'
                  size='small'
                  value={this.state.activity.minp}
                  onChangeCapture={(e) => this.handleNumberChange(e, 'minp')}
                />
              </Col>
              <Col xs='6' md='2'>
                <TextField
                  fullWidth
                  required
                  id='maxp-required'
                  label='Maksimimäärä'
                  size='small'
                  value={this.state.activity.maxp}
                  onChangeCapture={(e) => this.handleNumberChange(e, 'maxp')}
                />
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.activity.hasqueue}
                      onChange={(e) => this.handleSwitchChange(e, 'hasqueue')}
                    />
                  }
                  label='Jono käytössä'
                />
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.activity.autoCancellation}
                      onChange={(e) => this.handleSwitchChange(e, 'autoCancellation')}
                    />
                  }
                  label='Automaattinen peruutus'
                />
                <Typography variant='caption' color='textSecondary' display='block'>
                  Tapahtuma perutaan automaattisesti ilmoittautumisen päätyttyä, jos vähimmäisosallistujamäärä ei täyty.
                  Osallistujat saavat hyvityksen ja ilmoituksen.
                </Typography>
              </Col>
            </Row>
            <Row className='mt-4'>
              <Col xs='12' md='6'>
                <FormControlLabel
                  control={<Switch checked={!!this.state.activity.showusers} onChange={(e) => this.handleSwitchChange(e, 'showusers')} />}
                  label='Näytä ilmoittautuneiden nimet'
                />
              </Col>
            </Row>
            <Row className='mt-2'>
              <Col xs='12' md='6'>
                <FormControlLabel
                  control={<Switch checked={!!this.state.activity.hideentrycount} onChange={(e) => this.handleSwitchChange(e, 'hideentrycount')} />}
                  label='Piilota ilmoittautuneiden määrät'
                />
              </Col>
            </Row>
          </React.Fragment>
        )}
        <Row className='mt-4'>
          <Col xs='12' md='10'>
            <Typography variant='subtitle2' gutterBottom>
              Lisätietokysymykset (kysytään asiakkaalta ilmoittautuessa)
            </Typography>
            <ExtraInfoQuestionsEditor
              value={this.state.activity.extrainfo}
              onChange={(value) => this.handleArrayChange(value, 'extrainfo')}
            />
          </Col>
        </Row>
      </div>
    )
  }

  togglePosterEdit = () => {
    this.setState({ posterEditOpen: !this.state.posterEditOpen })
  }

  renderDescriptions = () => {
    return (
      <div>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <TextField
              required
              multiline={true}
              fullWidth
              minRows={2}
              rows={2}
              id='ingress-fi'
              label='Lyhyt kuvaus'
              size='small'
              value={this.state.activity.ingress.fi}
              variant='outlined'
              onChange={(e) => this.handleTextChange(e, 'ingress.fi')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <TextField
              required
              multiline={true}
              fullWidth
              minRows={5}
              rows={10}
              id='description-fi'
              label='Pitkä kuvaus'
              size='small'
              value={this.state.activity.description.fi}
              variant='outlined'
              onChange={(e) => this.handleTextChange(e, 'description.fi')}
            />
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            <Button variant='contained' color='primary' onClick={this.togglePosterEdit}>
              Valitse kuvituskuva
            </Button>
            {this.state.activity.media?.id ? (
              <div>
                <img src={this.state.activity.media.url} alt={this.state.activity.media.originalName} width='100px' />
              </div>
            ) : null}
            {/* <TextField
              id='standard-select-poster'
              select
              required
              label='Poster'
              value={this.state.activity.media?.id}
              helperText='Valitse kuvituskuva'
              onChange={(e) => this.handlePosterChange(e)}
            >
              {this.props.images.map((image) => (
                <MenuItem key={image.id} value={image.id}>
                  <img src={image.url} alt={image.originalName} width='20px' height='20px' />
                  {image.originalName}
                </MenuItem>
              ))}
            </TextField> */}
          </Col>
        </Row>
      </div>
    )
  }

  toggleTemplateResource = (resourceid) => {
    const { activity } = this.state
    const current = activity.resourceids || []
    const resourceids = current.includes(resourceid) ? current.filter((id) => id !== resourceid) : [...current, resourceid]
    this.setState({ activity: { ...activity, resourceids } })
  }

  renderTemplatePublish = () => {
    const { activity } = this.state
    const attaching = !!(this.props.customerdata && this.props.customerdata.features && this.props.customerdata.features.activityattaching)
    const resources = ((this.props.customerdata && this.props.customerdata.resources) || []).filter((r) => r.type === 'court' || !r.type)
    return (
      <div>
        <Row className='mt-4'>
          <Col xs='12' md='8'>
            <FormControlLabel
              control={<Switch checked={!!activity.publish} onChange={(e) => this.setState({ activity: { ...activity, publish: e.target.checked } })} />}
              label='Julkaise heti'
            />
            <Typography variant='caption' color='textSecondary' display='block'>
              {activity.publish
                ? 'Pohjasta luotu aktiviteetti julkaistaan heti.'
                : 'Pohjasta luotu aktiviteetti jää luonnokseksi.'}
            </Typography>
          </Col>
        </Row>
        {attaching ? (
          <Row className='mt-4'>
            <Col xs='12' md='8'>
              <Typography variant='subtitle2'>Oletuskentät</Typography>
              <Typography variant='caption' color='textSecondary' display='block'>
                Nämä kentät varataan aktiviteetin ajalle, kun pohja pudotetaan kalenteriin. Varaukset tehdään
                alla valitun maksajan nimiin; ohjaaja ei voi olla varauksen haltija.
              </Typography>
              <div className='mt-1'>
                {resources.map((r) => (
                  <FormControlLabel
                    key={r.id}
                    control={
                      <Switch
                        size='small'
                        checked={(activity.resourceids || []).includes(r.id)}
                        onChange={() => this.toggleTemplateResource(r.id)}
                      />
                    }
                    label={r.displayName || r.name || r.id}
                  />
                ))}
              </div>
              {(activity.resourceids || []).length > 0 ? (
                <div className='mt-2' style={{ maxWidth: 420 }}>
                  <PersonAndOrganisationSearch
                    label='Kenttävarausten maksaja'
                    placeholder='Etsi henkilö tai yritys'
                    values={activity.reservationpayers || []}
                    onChange={(event, value) => this.setState({ activity: { ...this.state.activity, reservationpayers: [...(value || [])] } })}
                  />
                  {(activity.reservationpayers || []).map((p) => (
                    <Typography key={p.id} variant='caption' color='textSecondary' display='block'>
                      {p.businessId ? p.name + ' (' + p.businessId + ')' : (p.firstname || '') + ' ' + (p.lastname || '')}
                      {p.email ? ', ' + p.email : ''}
                    </Typography>
                  ))}
                  {(activity.reservationpayers || []).length === 0 ? (
                    <Typography variant='caption' color='error' display='block'>
                      Ilman maksajaa kenttiä ei varata pudotettaessa.
                    </Typography>
                  ) : null}
                </div>
              ) : null}
              {(activity.resourceids || []).length > 0 ? (
                <TextField
                  className='mt-2'
                  size='small'
                  id='reservationcost'
                  label='Kenttävarauksen hinta'
                  type='number'
                  value={activity.reservationcost || 0}
                  onChange={(e) => this.setState({ activity: { ...activity, reservationcost: parseFloat(e.target.value) || 0 } })}
                  InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
                />
              ) : null}
            </Col>
          </Row>
        ) : null}
      </div>
    )
  }

  renderPublish = () => {
    if (this.props.template) return this.renderTemplatePublish()
    return (
      <div>
        <Row className='mt-4'>
          <Col xs='12' md='6'>
            {/* <FormControlLabel
              control={
                <Switch
                  checked={this.state.activity.ispublished}
                  onChange={(e) => this.handleSwitchChange(e, 'ispublished')}
                />
              }
              label='Julkaise'
            /> */}
            {/* one-off only: a pinned series would pin every occurrence */}
            {this.props.recurrent ? null : (
              <FormControlLabel
                control={<Switch checked={!!this.state.activity.pinned} onChange={(e) => this.handleSwitchChange(e, 'pinned')} />}
                label='Nosta tapahtuma ylimmäksi asiakasnäkymässä'
              />
            )}
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              id='standard-select-state'
              select
              required
              label='Tila'
              value={this.state.activity.state}
              helperText='Aktiviteetin tila'
              onChange={(e) => this.handleTextChange(e, 'state')}
            >
              {STATES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
        </Row>
      </div>
    )
  }

  // Kuvaukset is the last step for both flows and carries the state field that used to
  // sit alone on a step of its own.
  renderDescriptionsAndPublish = () => (
    <>
      {this.renderDescriptions()}
      {this.renderPublish()}
    </>
  )

  getStepContent = (step) => {
    switch (step) {
      case 0:
        return this.props.recurrent ? this.renderRecurrentBasicInfo() : this.renderBasicInfo()
      case 1:
        return this.props.recurrent ? this.renderRecurrencyInfo() : this.renderEntryInfo()
      case 2:
        return this.props.recurrent ? this.renderEntryInfo() : this.renderDescriptionsAndPublish()
      case 3:
        return this.props.recurrent ? this.renderDescriptionsAndPublish() : 'Unknown step'
      default:
        return 'Unknown step'
    }
  }

  render() {
    const { activeStep } = this.state

    return (
      <div style={{ padding: 20, backgroundColor: 'white' }}>
        <Stepper activeStep={activeStep}>
          {this.state.steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div>
          {activeStep === this.state.steps.length ? (
            <Row className='mt-5'>
              <Col xs='12' md='12'>
                <div>
                  <Typography>{this.props.template ? 'Pohja tallennettu' : 'Aktiviteetti luotu'}</Typography>
                  {this.props.template ? null : <Button onClick={this.handleReset}>Tee uusi</Button>}
                </div>
              </Col>
            </Row>
          ) : (
            <div>
              {this.getStepContent(activeStep)}
              <Row className='mt-5'>
                <Col xs='12' md='12'>
                  <div sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    <div sx={{ flex: '1 1 auto' }} />
                    <Button disabled={activeStep === 0} onClick={this.handleBack}>
                      Back
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={this.handleNext}
                      disabled={!this.state.activity.gender || this.state.activity.gender === '' || !!this.formError()}
                    >
                      {activeStep === this.state.steps.length - 1 ? (this.props.template ? 'Tallenna pohja' : 'Tallenna aktiviteetti') : 'Next'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
          {/* <div>
            <pre>{JSON.stringify(this.state.activity, null, 2)}</pre>
          </div> */}
        
        <Dialog
          fullWidth={true}
          maxWidth={'sm'}
          open={this.state.posterEditOpen}
          onClose={this.togglePosterEdit}
          aria-labelledby='max-width-dialog-title'
          style={{ zIndex: '1000' }}
        >
          <DialogTitle id='max-width-dialog-title'>Valitse kuva</DialogTitle>
          <DialogContent>
            <ImageSelector images={this.props.images} onSelect={this.handlePosterChange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.togglePosterEdit} color='primary'>
              Sulje
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

export default withFirebase(ActivityCreator)
